import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { Guid, UsageCounts as UsageCountsRecord } from "@/api/types";

export interface UsageCounts {
  /** vendorId -> number of entries this tenant has recorded against it. */
  vendors: ReadonlyMap<Guid, number>;
  /** accountId -> number of entries this tenant has recorded against it. */
  accounts: ReadonlyMap<Guid, number>;
}

const EMPTY: UsageCounts = { vendors: new Map(), accounts: new Map() };

/**
 * Real usage counts for the phone quick-entry pickers (docs/demo/tradeshow-2026-09.md
 * "Visitor journey" — the target is a stranger finishing in ~10 seconds).
 *
 * 8 vendors and 19 expense categories rendered flat cost ~1.85 screens of
 * scroll before the Amount step is even reachable. `QuickEntry.tsx` collapses
 * both pickers to a short "most used first" default with a "show more"
 * expander, and this hook is where "most used" comes from — never a hardcoded
 * guess, and never alphabetical order dressed up as relevance.
 *
 * feature/auth: this reads `GET /api/demo/usage-counts` (server-side aggregate
 * counts), NOT `GET /api/demo/entries` any more. `/entries` returns the tenant's
 * real financial detail — amounts, memos, timestamps — and moved behind a
 * session (DemoEndpoints.cs), which this anonymous, no-login page can never
 * have. `usage-counts` is a purpose-built, low-sensitivity public endpoint that
 * answers exactly the one question this picker needs ("which vendor/account has
 * this tenant used most") without exposing anything else. See
 * DemoRepository.GetUsageCountsAsync's remarks for the full reasoning.
 *
 * ONE FETCH ON MOUNT, not a live subscription. This page is loaded fresh per
 * visitor — a new QR scan opens a new tab — so "correct as of page load" is
 * correct for the entire session one visitor spends on it, and it stays
 * correct across the show because every new visitor reloads it fresh as
 * later entries accumulate. The laptop dashboard (`useEntries.ts`) is the one
 * screen that genuinely needs to update live while it stays open for hours;
 * this one does not, and a live subscription here would be needless overhead
 * on the one screen that most needs to load fast on a stranger's phone.
 *
 * Never blocks or errors the flow: a failed fetch degrades to "no usage data
 * yet" (empty maps, `orderByUsage` treats every item as tied at 0 and falls
 * back to the API's own order) rather than an error state — a visitor must
 * still be able to complete an entry even if this particular call fails.
 */
export function useUsageCounts(): UsageCounts {
  const [counts, setCounts] = useState<UsageCounts>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      try {
        const raw: UsageCountsRecord = await demoApi.getUsageCounts(controller.signal);
        if (cancelled) return;
        setCounts({
          vendors: new Map(Object.entries(raw.vendors)),
          accounts: new Map(Object.entries(raw.accounts)),
        });
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        console.warn(
          "Could not load usage counts — vendor/category pickers fall back to unordered lists.",
          err,
        );
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return counts;
}
